import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralItems} from '../utils/plural';

interface AutopilotCandidate {
    id: string;
    name: string;
    reason: string | null;
}

interface Preview {
    autopilotLevel: string | null;
    eligible: boolean;
    candidateCount: number;
    candidates: AutopilotCandidate[];
    skippedReason: string | null;
}

interface Props {
    ownerId: string | null;
    /** Запуск возможен только в собственном гардеробе. */
    canRun: boolean;
}

/**
 * Кандидаты, которых автопилот касается, и ручной запуск. Состояние самого автопилота
 * (расписание, фоновая задача, режим автономии) живёт в `AutopilotStatusBlock` — здесь
 * его не дублируем, иначе экран снова начнёт противоречить сам себе.
 */
const AutopilotPanel: React.FC<Props> = ({ownerId, canRun}) => {
    const [data, setData] = useState<Preview | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [running, setRunning] = useState(false);
    const [moved, setMoved] = useState<number | null>(null);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<Preview>('/autopilot/preview', {params});
            setData(res.data);
        } catch (e) {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [ownerId]);

    useEffect(() => {
        load();
    }, [load]);

    const run = async () => {
        setRunning(true);
        setError(false);
        setMoved(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<{ executed: boolean; movedCount: number }>('/autopilot/run', {});
            setMoved(res.data.movedCount);
            await load();
        } catch (e) {
            setError(true);
        } finally {
            setRunning(false);
        }
    };

    if (loading || failed || !data) {
        return null;
    }

    const s = data;

    return (
        <section
            data-testid="autopilot-panel"
            className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3"
        >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Кандидаты на выбытие
            </h2>

            {s.eligible ? (
                <>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Автопилот отправит в «Ждут передачи» {pluralItems(s.candidateCount)}, которые залежались
                        (обратимо — можно восстановить).
                    </p>
                    {canRun && (
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={run}
                                disabled={running || s.candidateCount === 0}
                                className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            >
                                {running ? 'Выполняется…' : 'Запустить автопилот'}
                            </button>
                            {moved != null && (
                                <span className="text-emerald-600 dark:text-emerald-400" data-testid="autopilot-moved">
                                    Решение принято: {moved}
                                </span>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="autopilot-disabled">
                    {s.skippedReason
                        ? s.skippedReason
                        : `Залежавшихся вещей: ${s.candidateCount}. `
                        + 'Решение по ним система предложит подтвердить в рекомендациях.'}
                </p>
            )}

            {error && (
                <p className="text-rose-600 dark:text-rose-400" data-testid="autopilot-error">
                    Не удалось запустить автопилот. Попробуйте ещё раз.
                </p>
            )}
        </section>
    );
};

export default AutopilotPanel;
